// Source-agnostic attachments step: pinned drop zone + scrollable existing-file list.
import { Stack, Text, ActionIcon, Tooltip } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { FONT_WEIGHT_SEMIBOLD, ICON_SIZE_SM } from '@/constants/ui';
import { FileUpload } from '@/components/common/FileUpload';
import { FormSection } from '@/components/common/form';
import { AttachmentRow } from '@/components/common/AttachmentRow';
import { FILE_VALIDATION_CONFIG } from '@/utils/fileValidation';
import type { AttachmentsFormSectionProps } from './AttachmentsFormSection.types';

/**
 * Reusable attachments form section. Wraps `FormSection` + `FileUpload`
 * (pinned drop zone, multi-select) plus an "existing files" list rendered via
 * `AttachmentRow`. Fully source-agnostic — accepts a generic existing-attachment
 * shape and optional delete callback so any feature (tasks, obstructions, …)
 * can reuse it without coupling to a specific API.
 */
export const AttachmentsFormSection = ({
  files,
  onFilesChange,
  existingAttachments = [],
  onDeleteExistingAttachment,
  isLoading = false,
  title,
  description,
  existingFilesLabel,
  error,
}: AttachmentsFormSectionProps) => {
  const { t } = useTranslation();
  const hasExistingAttachments = existingAttachments.length > 0;

  return (
    <FormSection title={title} description={description}>
      <FileUpload
        layout="pinned"
        files={files}
        onFilesChange={onFilesChange}
        mode="multi"
        disabled={isLoading}
        error={error}
        maxSize={FILE_VALIDATION_CONFIG.MAX_FILE_SIZE}
        hasExistingFiles={hasExistingAttachments}
      >
        {/* Existing files (edit mode) — scroll alongside the new uploads */}
        {hasExistingAttachments && (
          <Stack gap="xs" mt="md">
            {existingFilesLabel && (
              <Text size="sm" fw={FONT_WEIGHT_SEMIBOLD}>
                {existingFilesLabel}
              </Text>
            )}
            {existingAttachments.map((attachment) => (
              <AttachmentRow
                key={attachment.id}
                name={attachment.name}
                size={attachment.size}
                date={attachment.uploadedAt}
                mimeType={attachment.type}
                actions={
                  onDeleteExistingAttachment ? (
                    <Tooltip label={t('attachments.deleteAttachment')}>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() =>
                          onDeleteExistingAttachment(attachment.id)
                        }
                        disabled={isLoading}
                      >
                        <IconTrash size={ICON_SIZE_SM} />
                      </ActionIcon>
                    </Tooltip>
                  ) : undefined
                }
              />
            ))}
          </Stack>
        )}
      </FileUpload>
    </FormSection>
  );
};
